import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { GetByPersonAsync } from "@application/features/importantInformation/importantInfoThunk";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { alpha, Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { SteperHeader } from "@ui/components/Header/SteperHeader";
import { HCheckbox } from "@ui/components/inputs/HCheckbox";
import { HInput } from "@ui/components/inputs/HInput";
import { HTabInput } from "@ui/components/inputs/HTabInput";
import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
export const ImportantInfoForm = ({ control, errors, personId, formMod = "edit", inDrawer, onImportantInfoChange, setValue, eventType, }) => {
    const { importantInfo } = useAppSelector((state) => state.importantInfo);
    const { dietaryOptions } = useAppSelector((state) => state.dietaryOptions);
    const termsAccepted = useWatch({
        control,
        name: "ImportantInfo.TermsAccepted",
    });
    const dispatch = useAppDispatch();
    const isBusTransportRequired = useWatch({
        control,
        name: "ImportantInfo.IsBusTransportRequired",
    });
    const { t } = useTranslation();
    // Watch for personal info values to calculate total people
    const selectedOption = useWatch({
        control,
        name: "PersonalInfo.Option",
    });
    const adultsCount = useWatch({
        control,
        name: "PersonalInfo.AdultsCount",
    }) || 0;
    const childrenCount = useWatch({
        control,
        name: "PersonalInfo.ChildrenCount",
    }) || 0;
    const dietaryValue = useWatch({
        control,
        name: "ImportantInfo.DietaryRequirements",
        defaultValue: undefined,
    });
    const comments = useWatch({
        control,
        name: "ImportantInfo.Comments",
        defaultValue: undefined,
    });
    // Calculate total number of people (1 for main person + adults + children)
    const totalPeople = useMemo(() => {
        let total = 1; // Main person
        if (selectedOption === 2) {
            total += 1; // For option 2, only add 1 adult
        }
        else if (selectedOption === 3) {
            total += Number(adultsCount) + Number(childrenCount);
        }
        return total;
    }, [selectedOption, adultsCount, childrenCount]);
    // Generate seat count options based on total people
    const seatCountOptions = useMemo(() => {
        return Array.from({ length: totalPeople }, (_, i) => ({
            label: String(i + 1),
            value: String(i + 1),
        }));
    }, [totalPeople]);
    const { personalInfo } = useAppSelector((state) => state.person);
    const isRegistered = personalInfo?.invitationStatus === 3 || formMod === "view";
    useEffect(() => {
        if (isBusTransportRequired !== "true") {
            setValue("ImportantInfo.SeatsCount", undefined); // Reset the value
        }
    }, [isBusTransportRequired, setValue]);
    useEffect(() => {
        if (personId) {
            dispatch(GetByPersonAsync(personId))
                .unwrap()
                .then((response) => {
                onImportantInfoChange(response.id);
            });
        }
    }, [personId]);
    useEffect(() => {
        if (importantInfo) {
            setValue("ImportantInfo.IsBusTransportRequired", importantInfo.isBusTransportRequired);
            setValue("ImportantInfo.SeatsCount", importantInfo.seatsCount);
            setValue("ImportantInfo.TermsAccepted", importantInfo.termsAccepted);
            // Map dietaryRequirement IDs to corresponding dietaryOptions
            // const selectedDietaryOptions = importantInfo.dietaryRequirements
            //   ?.map((id) => dietaryOptions?.find((option) => option.value === id))
            //   .filter(Boolean); // Remove undefined values
            //setValue("ImportantInfo.DietaryRequirements", selectedDietaryOptions);
            setValue("ImportantInfo.Comments", importantInfo.comments);
            setValue("ImportantInfo.DisabilityComments", importantInfo.disabilityComments);
            setValue("ImportantInfo.DietaryRequirements", importantInfo.dietaryRequirements);
            setValue("ImportantInfo.IsDisability", String(importantInfo.isDisability));
        }
    }, [importantInfo, setValue]);
    const getPrivacyDocUrl = () => {
        if (process.env.NODE_ENV === "production") {
            return process.env.REACT_APP_PRIVACY_DOC_PROD;
        }
        else if (process.env.NODE_ENV === "test") {
            return process.env.REACT_APP_PRIVACY_DOC_TEST;
        }
        return process.env.REACT_APP_PRIVACY_DOC_DEV;
    };
    const isDisability = useWatch({
        control,
        name: "ImportantInfo.IsDisability",
    });
    return (_jsx(Box, { height: inDrawer ? undefined : "100%", children: _jsxs(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: 3,
            }, children: [_jsx(SteperHeader, { title: t("event.importantInformation") }), eventType === "family" ? (_jsx(Typography, { variant: "body1", color: "#00244D", children: t("event.importantInfoNote") })) : (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, children: [_jsx(Typography, { variant: "body1", color: "#00244D", children: t("event.directorImportantInfoNote") }), _jsxs(Box, { sx: {
                                border: (theme) => `1px solid ${alpha(theme.palette.edark.main, 0.2)}`,
                                borderColor: (theme) => errors?.ImportantInfo?.IsDisability?.message
                                    ? theme.palette.error.main
                                    : alpha(theme.palette.edark.main, 0.2),
                                borderRadius: 2,
                                px: 2,
                                py: 1.5,
                                height: "auto",
                                pb: 1.5,
                                backgroundColor: (theme) => alpha(theme.palette.edark.main, 0.04),
                            }, children: [_jsx(HTabInput, { disabled: isRegistered, control: control, error: errors?.ImportantInfo?.IsDisability?.message, label: eventType === "family"
                                        ? t("formFields.disabilityComments")
                                        : t("formFields.areYouDisabled"), name: "ImportantInfo.IsDisability", options: [
                                        {
                                            label: t("common.yes"),
                                            value: "true",
                                        },
                                        {
                                            label: t("common.no"),
                                            value: "false",
                                        },
                                    ], rules: {
                                        required: {
                                            value: true,
                                            message: "validations.chooseOption",
                                        },
                                    } }), isDisability === "true" && (_jsx(Box, { mt: 2, children: _jsx(HInput, { multiLines: true, backgroundColor: "white", rows: 3, control: control, name: "ImportantInfo.DisabilityComments", placeholder: t("event.disabilityComments"), disabled: isRegistered, rules: {
                                            required: {
                                                value: true,
                                                message: 'validations.required'
                                            }
                                        }, error: errors.ImportantInfo?.DisabilityComments?.message || "" }) }))] }), _jsx(Typography, { variant: "body1", color: "#00244D", children: t("event.importantInfoNote_1") })] })), _jsxs(Grid, { container: true, spacing: 2, children: [eventType === "family" && (_jsx(Grid, { size: 12, children: _jsxs(Box, { sx: {
                                    border: (theme) => `1px solid ${alpha(theme.palette.edark.main, 0.2)}`,
                                    borderColor: (theme) => errors?.ImportantInfo?.IsDisability?.message
                                        ? theme.palette.error.main
                                        : alpha(theme.palette.edark.main, 0.2),
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1.5,
                                    height: "auto",
                                    pb: 1.5,
                                    backgroundColor: (theme) => alpha(theme.palette.edark.main, 0.04),
                                }, children: [_jsx(HTabInput, { disabled: isRegistered, control: control, error: errors?.ImportantInfo?.IsDisability?.message, label: eventType === "family"
                                            ? t("formFields.disabilityComments")
                                            : t("formFields.areYouDisabled"), name: "ImportantInfo.IsDisability", options: [
                                            {
                                                label: t("common.yes"),
                                                value: "true",
                                            },
                                            {
                                                label: t("common.no"),
                                                value: "false",
                                            },
                                        ], rules: {
                                            required: {
                                                value: true,
                                                message: "validations.chooseOption",
                                            },
                                        } }), isDisability === "true" && (_jsx(Box, { mt: 2, children: _jsx(HInput, { multiLines: true, backgroundColor: "white", rows: 3, control: control, name: "ImportantInfo.DisabilityComments", label: t("event.disabilityComments"), disabled: isRegistered }) }))] }) })), eventType === "family" && (_jsx(Grid, { size: 12, children: _jsx(Box, { children: _jsx(HInput, { label: t("formFields.comments"), control: control, name: "ImportantInfo.Comments", multiLines: true, rows: 3, disabled: isRegistered }) }) })), eventType !== "family" && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: 12, children: _jsxs(Box, { sx: {
                                            border: "1px solid #00244D33",
                                            borderRadius: 2,
                                            px: 2.5,
                                            py: 2,
                                            height: "auto",
                                            background: "#00244D0A",
                                            gap: 0.5,
                                        }, children: [_jsx(Typography, { variant: "subtitle1", color: "#00244D", children: t("formFields.foodAlergiesDietryRequirementsLabel") }), _jsx(HInput, { multiLines: true, backgroundColor: "white", rows: 3, control: control, name: "ImportantInfo.DietaryRequirements", disabled: isRegistered })] }) }), _jsx(Grid, { size: 12, children: _jsxs(Box, { sx: {
                                            border: "1px solid #00244D33",
                                            borderRadius: 2,
                                            px: 2.5,
                                            py: 2,
                                            height: "auto",
                                            background: "#00244D0A",
                                            gap: 0.5,
                                        }, children: [_jsx(Typography, { variant: "subtitle1", color: "#00244D", children: t("formFields.additionalComments") }), _jsx(HInput, { control: control, backgroundColor: "white", name: "ImportantInfo.Comments", multiLines: true, rows: 3, disabled: isRegistered })] }) })] }))] }), _jsx(Box, { sx: {
                        mb: 3,
                    }, children: _jsx(HCheckbox, { control: control, name: "ImportantInfo.TermsAccepted", checked: termsAccepted, errormsg: errors?.ImportantInfo?.TermsAccepted?.message, rules: {
                            required: {
                                value: true,
                                message: "validations.termsAndConditionsRequired",
                            },
                        }, disabled: isRegistered, label: _jsxs(_Fragment, { children: [t("formFields.iagreeTerms"), " ", _jsx("span", { style: {
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        color: "blue",
                                    }, 
                                    // onClick={getPrivacyDocUrl}
                                    onClick: () => window.open("/assets/privacy_document/PRIVACYVERKLARING.pdf", "_blank"), children: t("formFields.privacyStatement") }), t("formFields.and"), " "] }) }) })] }) }));
};
{
    /* {!(eventType === "family") && (
              <Grid size={12}>
                <Typography variant="subtitle2" color="#00244D">
                  {t("event.dietaryRequirements")}
                </Typography>
              </Grid>
            )} */
}
{
    /* {eventType === "family" && (
              <>
                <Grid size={12}>
                  <HSelect
                    control={control}
                    label={t("event.wantBusTransport")}
                    name="ImportantInfo.IsBusTransportRequired"
                    options={[
                      { label: t("event.busOption1"), value: "true" },
                      { label: t("event.busOption2"), value: "false" },
                      { label: t("event.busOption3"), value: "false" },
                    ]}
                    disabled={isRegistered}
                  />
                </Grid>
                {doYouWantTransport === "true" && (
                  // <Grid size={12}>
                  //   <HSelect
                  //     control={control}
                  //     label={t("formFields.howManySeats")}
                  //     name="ImportantInfo.SeatsCount"
                  //     options={seatCountOptions}
                  //     disabled={
                  //       control._formValues?.ImportantInfo
                  //         ?.IsBusTransportRequired !== "true"
                  //     }
                  //   />
                  // </Grid>
                  // <Grid size={12}>
                  //   <Typography
                  //     variant="subtitle1"
                  //     fontWeight={500}
                  //     color="#00244D"
                  //   >
                  //     {t("formFields.howManyAdults")}
                  //   </Typography>
                  //   <Box sx={{ ...colCenter, alignItems: "flex-start", gap: 1 }}>
                  //     <HInput
                  //       label={""}
                  //       type="number"
                  //       control={control}
                  //       name="PersonalInfo.AdultsCount"
                  //       error={errors.PersonalInfo?.AdultsCount?.message}
                  //       rules={{
                  //         required: {
                  //           value: true,
                  //           message: t("validations.numberOfAdultsRequired"),
                  //         },
                  //       }}
                  //       disabled={isRegistered}
                  //     />
                  //     <EIconButton
                  //       height="52px"
                  //       onClick={() =>
                  //         setValue(
                  //           "PersonalInfo.AdultsCount",
                  //           Math.max(adultsCount - 1, 0)
                  //         )
                  //       }
                  //     >
                  //       <Minus />
                  //     </EIconButton>
                  //     <EIconButton
                  //       height="52px"
                  //       onClick={() =>
                  //         setValue("PersonalInfo.AdultsCount", adultsCount + 1)
                  //       }
                  //     >
                  //       <Add />
                  //     </EIconButton>
                  //   </Box>
                  // </Grid>
                )}
              </>
            )} */
}
{
    /* <Grid size={12}>
              <HAutocomplete
                label="Dietary Requirements"
                name="ImportantInfo.DietaryRequirements"
                control={control}
                options={
                  dietaryOptions?.map((x) => ({
                    label: x.label,
                    value: x.value,
                  })) ?? []
                }
                disabled={isRegistered}
              />
            </Grid> */
}
